import { renderValue } from '@thinkalpha/language-services';
import type { ConcreteUnit } from '@thinkalpha/language-services';
import type { ConditionAlertDefinition } from '@thinkalpha/platform-ws-client/contracts/alerts.js';
import type { IndicatorImportRef } from '@thinkalpha/platform-ws-client/contracts/dictionary.js';
import type { Condition } from '@thinkalpha/platform-ws-client/contracts/ideas/conditions.js';
import i18next from 'i18next';
import { IncludeOperatorMatrix } from 'src/features/scranner/dtos/ScrannerPlanDTO';

/**
 * Convert a Condition to a formula string and imports for table evaluation
 */
export function conditionToFormula(condition: Condition): { formula: string; imports: IndicatorImportRef[] } {
    switch (condition.type) {
        case 'formula': {
            return { formula: condition.formula, imports: condition.imports };
        }
        case 'membership': {
            if (!condition.selection) {
                throw new Error('Membership condition must have selection field populated');
            }
            const lhs = condition.selection.formula;
            const rhs = condition.invert ? 'false' : 'true';
            return { formula: `${lhs} == ${rhs}`, imports: condition.selection.imports };
        }
        case 'quantitative': {
            if (!condition.selection) {
                throw new Error('Quantitative condition must have selection field populated');
            }
            const lhs = condition.selection.formula;
            const imports = condition.selection.imports;
            const hasMin = condition.min !== null;
            const hasMax = condition.max !== null;

            if (hasMin && hasMax) {
                const minOp = IncludeOperatorMatrix[condition.min?.mode ?? 'inclusive'].greaterThan;
                const maxOp = IncludeOperatorMatrix[condition.max?.mode ?? 'inclusive'].lessThan;
                return {
                    formula: `(${lhs} ${minOp} ${condition.min!.value}) && (${lhs} ${maxOp} ${condition.max!.value})`,
                    imports,
                };
            } else if (hasMin) {
                const op = IncludeOperatorMatrix[condition.min?.mode ?? 'inclusive'].greaterThan;
                return { formula: `${lhs} ${op} ${condition.min!.value}`, imports };
            } else if (hasMax) {
                const op = IncludeOperatorMatrix[condition.max?.mode ?? 'inclusive'].lessThan;
                return { formula: `${lhs} ${op} ${condition.max!.value}`, imports };
            }
            throw new Error('Quantitative condition must have at least min or max');
        }
        case 'equality': {
            if (!condition.selection) {
                throw new Error('Equality condition must have selection field populated');
            }
            const lhs = condition.selection.formula;
            const imports = condition.selection.imports;
            if (condition.values.length === 0) {
                throw new Error('Equality condition must have at least one value');
            }
            const comparisons = condition.values.map((value) => `${lhs} == ${renderValue(value)}`);
            return {
                formula: comparisons.length === 1 ? comparisons[0] : `(${comparisons.join(' || ')})`,
                imports,
            };
        }
        default:
            throw new Error(`Unknown condition type: ${(condition as Condition).type}`);
    }
}

/**
 * Resolve condition selection by populating the selection field for quantitative conditions
 * that only have an indicator reference
 */
export function resolveConditionSelection(condition: Condition): Condition {
    if (condition.type !== 'quantitative') {
        return condition;
    }
    if (condition.selection?.formula) {
        return condition;
    }
    const indicatorRef = condition.indicator;
    if (!indicatorRef) {
        return condition;
    }
    const INDICATOR_ALIAS = '_ind';
    return {
        ...condition,
        selection: {
            selfAlias: INDICATOR_ALIAS,
            formula: `${INDICATOR_ALIAS}()`,
            imports: [{ id: indicatorRef.id, alias: INDICATOR_ALIAS }],
        },
    };
}

/**
 * Format a numeric value for display in toast notifications.
 * For percentages (0-1 scale), multiplies by 100 and adds % suffix.
 * For other values, shows exactly 2 decimal places.
 */
export function formatValueForDisplay(value: number, unit?: ConcreteUnit | null): string {
    if (unit === 'percent') {
        // Percentages are stored in 0-1 scale, multiply by 100 for display
        return `${(value * 100).toFixed(2)}%`;
    }
    return value.toFixed(2);
}

/**
 * Format a numeric value for TTS speech output.
 * Price values are read as dollars/cents to avoid "dot" pronunciation.
 */
function formatValueForSpeech(value: number, unit?: ConcreteUnit | null): string {
    if (unit === 'price') {
        // Price currently maps to locale-defined major/minor currency units
        // (e.g. dollars/cents in English). Instrument-specific currencies are not inferred here.
        const rounded = Number(value.toFixed(2));
        const absolute = Math.abs(rounded);
        const totalCents = Math.round(absolute * 100);
        const dollars = Math.floor(totalCents / 100);
        const cents = totalCents % 100;

        const dollarUnit = i18next.t('alerts::tts::currency-dollar', { count: dollars });
        const centUnit = i18next.t('alerts::tts::currency-cent', { count: cents });
        const signText =
            rounded < 0
                ? i18next.t('alerts::tts::currency-sign-negative')
                : i18next.t('alerts::tts::currency-sign-none');
        const normalizedSign = String(signText).trim();
        const signPrefix = normalizedSign ? `${normalizedSign} ` : '';

        if (cents === 0) {
            return i18next.t('alerts::tts::currency-amount-no-cents', {
                sign: signPrefix,
                dollars,
                dollarUnit,
            });
        }

        return i18next.t('alerts::tts::currency-amount', {
            sign: signPrefix,
            dollars,
            cents,
            dollarUnit,
            centUnit,
        });
    }

    return formatValueForDisplay(value, unit);
}

/**
 * Format a condition for human-readable display (e.g., in toast notifications).
 * Returns a string like "Last Price > 100", "Change Close % > 2.00%", etc.
 * @param condition The condition to format
 * @param indicatorName The human-readable indicator name (e.g., "Last Price", "Volume")
 * @param indicatorUnit The unit of the indicator (e.g., "percent", "price") for proper formatting
 */
export function formatConditionForDisplay(
    condition: Condition,
    indicatorName?: string,
    indicatorUnit?: ConcreteUnit | null,
): string {
    switch (condition.type) {
        case 'formula':
            return condition.formula;
        case 'quantitative': {
            const hasMin = condition.min !== null;
            const hasMax = condition.max !== null;
            const prefix = indicatorName ? `${indicatorName} ` : '';

            if (hasMin && hasMax) {
                return `${prefix}between ${formatValueForDisplay(condition.min!.value, indicatorUnit)} and ${formatValueForDisplay(condition.max!.value, indicatorUnit)}`;
            } else if (hasMin) {
                const op = condition.min!.mode === 'exclusive' ? '>' : '≥';
                return `${prefix}${op} ${formatValueForDisplay(condition.min!.value, indicatorUnit)}`;
            } else if (hasMax) {
                const op = condition.max!.mode === 'exclusive' ? '<' : '≤';
                return `${prefix}${op} ${formatValueForDisplay(condition.max!.value, indicatorUnit)}`;
            }
            return 'condition met';
        }
        case 'membership':
            const memberPrefix = indicatorName ? `${indicatorName} ` : '';
            return condition.invert ? `${memberPrefix}not in list` : `${memberPrefix}in list`;
        case 'equality':
            const eqPrefix = indicatorName ? `${indicatorName} ` : '';
            if (condition.values.length === 1) {
                return `${eqPrefix}= ${renderValue(condition.values[0])}`;
            }
            return `${eqPrefix}in (${condition.values.map((v) => renderValue(v)).join(', ')})`;
        default:
            return 'condition met';
    }
}

/**
 * Format a condition for TTS speech output.
 * Uses i18next translations for operators instead of symbols (e.g., "greater than" instead of ">").
 * Falls back to formatConditionForDisplay for non-quantitative condition types.
 */
export function formatConditionForSpeech(
    condition: Condition,
    indicatorName?: string,
    indicatorUnit?: ConcreteUnit | null,
): string {
    switch (condition.type) {
        case 'quantitative': {
            const hasMin = condition.min !== null;
            const hasMax = condition.max !== null;
            const prefix = indicatorName ? `${indicatorName} ` : '';

            if (hasMin && hasMax) {
                return `${prefix}${i18next.t('alerts::tts::range-between-and', {
                    minValue: formatValueForSpeech(condition.min!.value, indicatorUnit),
                    maxValue: formatValueForSpeech(condition.max!.value, indicatorUnit),
                })}`;
            } else if (hasMin) {
                const op =
                    condition.min!.mode === 'exclusive'
                        ? i18next.t('alerts::tts::operator-gt')
                        : i18next.t('alerts::tts::operator-gte');
                return `${prefix}${op} ${formatValueForSpeech(condition.min!.value, indicatorUnit)}`;
            } else if (hasMax) {
                const op =
                    condition.max!.mode === 'exclusive'
                        ? i18next.t('alerts::tts::operator-lt')
                        : i18next.t('alerts::tts::operator-lte');
                return `${prefix}${op} ${formatValueForSpeech(condition.max!.value, indicatorUnit)}`;
            }
            return 'condition met';
        }
        default:
            return formatConditionForDisplay(condition, indicatorName, indicatorUnit);
    }
}

/**
 * Check if a quantitative condition has an empty value (no min or max).
 */
export function hasEmptyConditionValue(condition: Condition): boolean {
    if (condition.type !== 'quantitative') return false;
    return condition.min === null && condition.max === null;
}

/**
 * Check if the condition has changed between two alert definitions in a way that
 * requires restarting monitoring (formula or imports changed).
 */
export function hasConditionChanged(oldAlert: ConditionAlertDefinition, newAlert: ConditionAlertDefinition): boolean {
    const oldEmpty = hasEmptyConditionValue(oldAlert.condition);
    const newEmpty = hasEmptyConditionValue(newAlert.condition);

    // If either is empty, consider it changed (caller should handle empty conditions)
    if (oldEmpty || newEmpty) {
        return oldEmpty !== newEmpty;
    }

    const oldCondition = resolveConditionSelection(oldAlert.condition);
    const newCondition = resolveConditionSelection(newAlert.condition);
    const { formula: oldFormula, imports: oldImports } = conditionToFormula(oldCondition);
    const { formula: newFormula, imports: newImports } = conditionToFormula(newCondition);

    if (oldFormula !== newFormula) {
        return true;
    }

    // Compare imports - if they differ, the condition uses different indicators
    if (oldImports.length !== newImports.length) {
        return true;
    }

    for (let i = 0; i < oldImports.length; i++) {
        const oldImport = oldImports[i];
        const newImport = newImports[i];
        if (oldImport.id !== newImport.id) {
            return true;
        }
    }

    return false;
}
